import { useCallback, useMemo } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import type { Person, PersonChecklist, ReadinessState } from '@vigeo/shared';
import { fetchChecklist } from '@/lib/api';

/**
 * Readiness for everyone in the directory, one derived checklist per person (checkpoint 7B).
 *
 * Each entry is the SAME query the Person Overview uses (`['checklist', id]`), so opening a
 * person costs nothing extra and the directory can never show a verdict the page would not.
 * Nothing is computed here: an entry is either the engine's checklist, still pending, or
 * unavailable — and the screen says so rather than guessing.
 */
export type ReadinessEntry =
  | { readonly status: 'pending' }
  | { readonly status: 'error' }
  /** Added by hand as existing staff: no checklist is derived, and none is requested (ADR-016 §4). */
  | { readonly status: 'untracked' }
  | { readonly status: 'ready'; readonly checklist: PersonChecklist };

const PENDING: ReadinessEntry = { status: 'pending' };
const ERROR: ReadinessEntry = { status: 'error' };
export const UNTRACKED: ReadinessEntry = { status: 'untracked' };

function toEntry(result: UseQueryResult<PersonChecklist>): ReadinessEntry {
  if (result.status === 'success') return { status: 'ready', checklist: result.data };
  return result.status === 'error' ? ERROR : PENDING;
}

// Module-level so TanStack can memoise the combined result between renders.
const combine = (results: UseQueryResult<PersonChecklist>[]): readonly ReadinessEntry[] => results.map(toEntry);

export interface PeopleReadiness {
  readonly entryOf: (id: string) => ReadinessEntry;
  readonly stateOf: (id: string) => ReadinessState | null;
  /** True once every person's checklist has either arrived or failed. */
  readonly settled: boolean;
  /** People whose verdict is "Needs review" — meaningful only once `settled`. */
  readonly needsReview: number;
}

export function usePeopleReadiness(people: readonly Person[]): PeopleReadiness {
  const entries = useQueries({
    queries: people.map((person) => ({
      queryKey: ['checklist', person.id],
      queryFn: () => fetchChecklist(person.id),
      // One directory visit is one derivation per person; the Person page refreshes on its own.
      staleTime: 60_000,
    })),
    combine,
  });

  const byId = useMemo(() => new Map(people.map((person, index) => [person.id, entries[index] ?? PENDING])), [people, entries]);

  const entryOf = useCallback((id: string) => byId.get(id) ?? PENDING, [byId]);
  const stateOf = useCallback(
    (id: string) => {
      const entry = byId.get(id);
      return entry?.status === 'ready' ? entry.checklist.state : null;
    },
    [byId],
  );

  return {
    entryOf,
    stateOf,
    settled: entries.every((entry) => entry.status !== 'pending'),
    needsReview: entries.filter((entry) => entry.status === 'ready' && entry.checklist.state === 'needs_review').length,
  };
}
